import type { MailDraftList } from "@uwu/contracts";
/**
 * [M4-09] Mail API client ของ webmail — ห่อ `/api/v1/mail/*` ของ M4-02/M4-02F (M4-CONTRACTS §4 Mail core)
 * - ใช้ `api` helper เดิม (cookie session + X-Requested-With)
 * - thread PATCH ต้องส่ง If-Match (ETag = `"<version>"`) เสมอ → 409 version_conflict เมื่อชนกัน
 * - [M4-02F merged #148] `archived` อยู่ใน MailThreadPatch แล้ว · `flags.webmail` บน accounts/me (optional, fail-closed)
 */
import type { MailAccountView, MailIdentity, MailLabel, MailMessageRaw, MailThreadDetail, MailThreadList, MailThreadPatch } from "@uwu/contracts";
import { api, qs } from "./api";

export type { MailIdentity };

/** accounts/me ของ M4-02F — `flags.webmail` optional: absent = ปิด (fail-closed) */
export type MailAccountMe = MailAccountView;

/** ธง mail.webmail ผ่าน accounts/me (M4-02F) — ไม่มี field = ปิด */
export function webmailEnabled(a: MailAccountMe): boolean {
  return a.flags?.webmail ?? false;
}

/**
 * [A3 · D-042] แนบ account_id selector ของ A1/PR167 ให้ account-scoped route
 * (additive-optional query param — ไม่ใส่ใน JSON body เพราะ server parseJson strict ต่อ unknown field)
 * undefined = กล่องเดียว/ไม่เลือก → URL เดิมไม่มี param (พฤติกรรมก่อน A3 คงเดิม)
 */
function scoped(path: string, accountId: string | undefined): string {
  if (accountId === undefined || accountId === "") return path;
  return path + (path.includes("?") ? "&" : "?") + "account_id=" + encodeURIComponent(accountId);
}

export const mailApi = {
  account: () => api.get<MailAccountMe>("/api/v1/mail/accounts/me"),
  labels: (accountId?: string) => api.get<{ items: MailLabel[] }>(scoped("/api/v1/mail/labels", accountId)),
  threads: (params: { label?: string; q?: string; cursor?: string; accountId?: string }) =>
    api.get<MailThreadList>(scoped("/api/v1/mail/threads" + qs({ label: params.label, q: params.q, cursor: params.cursor }), params.accountId)),
  thread: (id: string, accountId?: string) => api.get<MailThreadDetail>(scoped(`/api/v1/mail/threads/${encodeURIComponent(id)}`, accountId)),
  /** If-Match จำเป็นทุกครั้ง — version มาจาก summary/detail ที่โหลดไว้ (body strict: unknown field = 422) */
  patchThread: (id: string, patch: MailThreadPatch, version: number, accountId?: string) =>
    api.patch<MailThreadDetail>(scoped(`/api/v1/mail/threads/${encodeURIComponent(id)}`, accountId), patch, { headers: { "If-Match": `"${version}"` } }),
  /** DELETE ครั้งแรก = ย้ายไป trash (outcome:"trashed") · ครั้งที่สอง = ลบถาวร */
  deleteMessage: (id: string, accountId?: string) =>
    api.del<{ id: string; folder: "trash"; outcome: "trashed" | "purged" }>(scoped(`/api/v1/mail/messages/${encodeURIComponent(id)}`, accountId)),
  /** ลิงก์ .eml แบบ signed (อายุ 10 นาที) — ใช้ดาวน์โหลดข้อความเต็ม/ไฟล์แนบ */
  messageRaw: (id: string, accountId?: string) => api.get<MailMessageRaw>(scoped(`/api/v1/mail/messages/${encodeURIComponent(id)}/raw`, accountId)),
};

// ===========================================================================
// [M4-10] write side — compose/settings/filters/quarantine (M4-05/06/07)
// ต่อยอดจาก mailApi ของ M4-09 · queryOptions อยู่ที่ queries-mail.ts (ตาม pattern M4-09)
// ===========================================================================
import type {
  MailDraft,
  MailFilter,
  MailFilterAction,
  MailFilterConditions,
  MailMessage,
  MailSendResult,
  MailSettings,
  MailSettingsPatch,
  QuarantineItem,
} from "@uwu/contracts";

// worker-only shapes (ไม่ได้อยู่ใน @uwu/contracts — ตรงกับ apps/worker/src/mail/*)
export type MailFilterCreateInput = { ord?: number; conditions: MailFilterConditions; actions: MailFilterAction[]; enabled?: boolean };
export type MailFilterPatchInput = Partial<MailFilterCreateInput>;
export type MailFilterList = { items: (MailFilter & { log?: unknown[] })[] };

export type QuarantineListResponse = { items: QuarantineItem[]; next_cursor: string | null };
export type QuarantinePreview = {
  id: string;
  from_address: string | null;
  subject: string | null;
  snippet: string | null;
  score: number | null;
};
export type QuarantineReleaseResult = {
  id: string;
  released_at: string;
  released_by: string;
  message_id: string | null;
  thread_id: string | null;
};
export type MailSpamReportResult = { verdict: "spam" | "not_spam"; folder: string; folder_changed: boolean; trained: boolean };
export type MailUnsendResult = { id: string; delivery_status: "canceled" | "cancel_requested" };

// ตัวส่งเข้า API (input) — cc/bcc/attachment_ids มี default [] ฝั่ง server เฉพาะตอน create
// [M4-02G] PATCH partial ปลอดภัยแล้ว (field ที่ไม่ส่งคงค่าเดิม) แต่ Composer ของเรายังส่ง full snapshot
export type MailReplyContextInput = { message_id: string; type: "reply" | "reply_all" | "forward" };
export type MailAddressLike = { address: string; name?: string | null };
export type MailDraftCreateInput = {
  thread_id?: string | null;
  from_address: string;
  to?: MailAddressLike[];
  cc?: MailAddressLike[];
  bcc?: MailAddressLike[];
  subject?: string | null;
  body_html?: string | null;
  body_text?: string | null;
  attachment_ids?: string[];
  reply_context?: MailReplyContextInput | null;
};
export type MailDraftPatchInput = Partial<MailDraftCreateInput>;

/** [M4-02G] identity ที่ส่งได้จาก accounts/me — primary มาก่อน · fallback primary_address เมื่อ server เก่า (optional field) */
export function identitiesOf(account: MailAccountMe): MailIdentity[] {
  if (account.identities && account.identities.length > 0) return account.identities;
  return account.primary_address
    ? [{ id: account.primary_address_id ?? "00000000-0000-4000-8000-000000000000", address: account.primary_address, display: account.display_name, kind: "account", right: "account" }]
    : [];
}

// settings / labels — [A3] accountId = account selector ของ A1 (query param)
export function getMailSettings(accountId?: string): Promise<MailSettings> {
  return api.get<MailSettings>(scoped("/api/v1/mail/settings", accountId));
}
export function patchMailSettings(patch: MailSettingsPatch, accountId?: string): Promise<MailSettings> {
  return api.patch<MailSettings>(scoped("/api/v1/mail/settings", accountId), patch);
}
export function createMailLabel(input: { name: string; color?: string | null }, accountId?: string): Promise<MailLabel> {
  return api.post<MailLabel>(scoped("/api/v1/mail/labels", accountId), input);
}
export function patchMailLabel(id: string, input: { name?: string; color?: string | null }, accountId?: string): Promise<MailLabel> {
  return api.patch<MailLabel>(scoped(`/api/v1/mail/labels/${encodeURIComponent(id)}`, accountId), input);
}
export function deleteMailLabel(id: string, accountId?: string): Promise<{ id: string; deleted: boolean }> {
  return api.del<{ id: string; deleted: boolean }>(scoped(`/api/v1/mail/labels/${encodeURIComponent(id)}`, accountId));
}

// drafts + send (M4-05) — [A3] สร้าง/แจกแจงตามกล่องที่เลือก · ระดับ :id server อนุมานกล่องจากแถวของ resource
export function createMailDraft(input: MailDraftCreateInput, accountId?: string): Promise<MailDraft> {
  return api.post<MailDraft>(scoped("/api/v1/mail/drafts", accountId), input);
}
export function patchMailDraft(id: string, patch: MailDraftPatchInput, accountId?: string): Promise<MailDraft> {
  return api.patch<MailDraft>(scoped(`/api/v1/mail/drafts/${encodeURIComponent(id)}`, accountId), patch);
}
export function deleteMailDraft(id: string, accountId?: string): Promise<{ id: string; deleted: boolean }> {
  return api.del<{ id: string; deleted: boolean }>(scoped(`/api/v1/mail/drafts/${encodeURIComponent(id)}`, accountId));
}
export function listMailDrafts(accountId?: string): Promise<MailDraftList> {
  return api.get<MailDraftList>(scoped("/api/v1/mail/drafts", accountId));
}
export function getMailDraft(id: string, accountId?: string): Promise<MailDraft> {
  return api.get<MailDraft>(scoped(`/api/v1/mail/drafts/${encodeURIComponent(id)}`, accountId));
}
export function sendMailDraft(id: string, input: { send_at?: string; undo_seconds?: number }, accountId?: string): Promise<MailSendResult> {
  return api.post<MailSendResult>(scoped(`/api/v1/mail/drafts/${encodeURIComponent(id)}/send`, accountId), input);
}
export function unsendMailMessage(id: string, accountId?: string): Promise<MailUnsendResult> {
  return api.post<MailUnsendResult>(scoped(`/api/v1/mail/messages/${encodeURIComponent(id)}/unsend`, accountId));
}
export function cancelScheduledMailMessage(id: string, accountId?: string): Promise<MailUnsendResult> {
  return api.post<MailUnsendResult>(scoped(`/api/v1/mail/messages/${encodeURIComponent(id)}/cancel-scheduled`, accountId));
}
export function getMailMessage(id: string, accountId?: string): Promise<MailMessage> {
  return api.get<MailMessage>(scoped(`/api/v1/mail/messages/${encodeURIComponent(id)}`, accountId));
}

// filters (M4-06) — [A3] accountId ของ collection/create · :id อนุมานจากแถว
export function listFilters(accountId?: string): Promise<MailFilterList> {
  return api.get<MailFilterList>(scoped("/api/v1/mail/filters", accountId));
}
export function createMailFilter(input: MailFilterCreateInput, accountId?: string): Promise<MailFilter> {
  return api.post<MailFilter>(scoped("/api/v1/mail/filters", accountId), input);
}
export function patchMailFilter(id: string, input: MailFilterPatchInput, accountId?: string): Promise<MailFilter> {
  return api.patch<MailFilter>(scoped(`/api/v1/mail/filters/${encodeURIComponent(id)}`, accountId), input);
}
export function deleteMailFilter(id: string, accountId?: string): Promise<{ id: string; deleted: boolean }> {
  return api.del<{ id: string; deleted: boolean }>(scoped(`/api/v1/mail/filters/${encodeURIComponent(id)}`, accountId));
}

// quarantine (M4-07) — [A3] รายการของกล่องที่เลือก · preview/release/not-spam อนุมานจากแถว
// (self-release ไม่ใส่ account_id ใน JSON body — A1 ใช้ query param เฉพาะรายการ)
export function listQuarantine(accountId?: string): Promise<QuarantineListResponse> {
  return api.get<QuarantineListResponse>(scoped("/api/v1/mail/quarantine", accountId));
}
export function quarantinePreview(id: string, accountId?: string): Promise<QuarantinePreview> {
  return api.get<QuarantinePreview>(scoped(`/api/v1/mail/quarantine/${encodeURIComponent(id)}/preview`, accountId));
}
export function releaseQuarantineItem(id: string, accountId?: string): Promise<QuarantineReleaseResult> {
  return api.post<QuarantineReleaseResult>(scoped(`/api/v1/mail/quarantine/${encodeURIComponent(id)}/release`, accountId), {});
}
export function reportMailNotSpam(messageId: string, accountId?: string): Promise<MailSpamReportResult> {
  return api.post<MailSpamReportResult>(scoped(`/api/v1/mail/messages/${encodeURIComponent(messageId)}/not-spam`, accountId));
}
export function reportMailSpam(messageId: string, accountId?: string): Promise<MailSpamReportResult> {
  return api.post<MailSpamReportResult>(scoped(`/api/v1/mail/messages/${encodeURIComponent(messageId)}/report-spam`, accountId));
}
